import { BlankContent, Spinner } from '@cint/wonderment';
import { useEffect, useMemo } from 'react';

import { useFetchData } from '@/api/apiHooks';
import { URLS } from '@/utils/urls';

import { ReportFiltersSheet } from './ReportFiltersSheet';

interface RecruitmentReportFiltersProps {
  selectedPanel?: string;
  filters: Record<string, any>;
  onFiltersChange: (
    updatedFilters: Partial<RecruitmentReportFiltersProps['filters']>
  ) => void;
  selectedCompany: string;
}

export const RecruitmentReportFilters = ({
  filters,
  selectedPanel,
  onFiltersChange,
  selectedCompany,
}: RecruitmentReportFiltersProps) => {
  const {
    isLoading: isSourcesLoading,
    data: sourcesData,
    error: sourcesError,
  } = useFetchData<any>({
    url: URLS.getCampaignSources(selectedCompany),
    enabled: !!selectedCompany,
  });

  const sourcesOptions = useMemo(() => {
    if (sourcesData?.recruitment_sources) {
      return sourcesData.recruitment_sources.map((source) => ({
        label: source,
        id: source,
      }));
    }
    return [];
  }, [sourcesData?.recruitment_sources]);

  useEffect(() => {
    const sourceFilterState = filters.sourceFilterState;
    if (
      selectedPanel &&
      (!Array.isArray(sourceFilterState) || sourceFilterState.length === 0)
    ) {
      // we are preselecting top 5 sources if none are selected
      onFiltersChange({
        sourceFilterState: sourcesData?.recruitment_sources?.slice(0, 5),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPanel, sourcesData?.recruitment_sources]);

  if (sourcesError) {
    return (
      <BlankContent
        description="Please try again later"
        size="sm"
        title="Something went wrong"
      />
    );
  }

  if (isSourcesLoading) {
    return <Spinner />;
  }

  return (
    <ReportFiltersSheet
      filters={filters}
      onFiltersChange={onFiltersChange}
      selectedPanel={selectedPanel}
      filterOptions={sourcesOptions}
      filterKey="sourceFilterState"
      filterBy="source"
    />
  );
};
